import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import TeeDetailModal from '../components/TeeDetailModal';
import CancelTeeTimeModal from '../components/CancelTeeTimeModal';

const fmtDate = (d) => {
  if (!d) return { m: '???', d: '?' };
  const dateStr = d.includes('T') ? d.split('T')[0] : d;
  const [year, month, day] = dateStr.split('-').map(Number);
  const dt = new Date(year, month - 1, day);
  return { m: dt.toLocaleDateString('en-US', { month: 'short' }), d: dt.getDate() };
};
const fmtTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

// Date "since" presets — value is number of days back, or null for "all time"
const RANGES = [
  { label: 'Last 90 days', days: 90  },
  { label: 'Last year',    days: 365 },
  { label: 'All time',     days: null },
];

const sinceFromDays = (days) => {
  if (days == null) return '2000-01-01';
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
};

export default function MySchedulePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('upcoming'); // 'upcoming' | 'past'
  const [rangeIdx, setRangeIdx] = useState(0);
  const [teeTimes, setTeeTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [cancelTT, setCancelTT] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'upcoming') {
        const all = await api.getTeeTimes();
        setTeeTimes(all.filter(tt =>
          tt.players?.some(p => p.id === user.id) || tt.host_id === user.id
        ));
      } else {
        const since = sinceFromDays(RANGES[rangeIdx].days);
        const past = await api.getPastTeeTimes(since);
        setTeeTimes(past);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [user.id, tab, rangeIdx]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel your spot? The host will be notified.')) return;
    try { await api.cancelSpot(id); load(); } catch (err) { alert(err.message); }
  };

  const handleCancelTeeTime = (tt) => {
    setCancelTT(tt);
  };

  const detailTT = teeTimes.find(tt => tt.id === detailId);
  const isPast = tab === 'past';

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'1rem', flexWrap:'wrap', gap:'.75rem' }}>
        <div>
          <h2 style={{ fontSize: 22 }}>My schedule</h2>
          <p style={{ fontSize: 12, color: '#7a7a6a', marginTop: 1 }}>
            {isPast ? 'Tee times you played in the past' : 'Posted or joined tee times'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap: 8, marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display:'flex', gap: 0, background:'#f4f1eb', borderRadius: 20, padding: 3 }}>
          <button
            onClick={() => setTab('upcoming')}
            style={{
              fontSize: 12, padding: '5px 14px', borderRadius: 17, border: 'none', cursor: 'pointer',
              background: tab === 'upcoming' ? '#0d2b1a' : 'transparent',
              color:      tab === 'upcoming' ? '#7ec99a' : '#5a5a4a',
              fontWeight: tab === 'upcoming' ? 500 : 400,
            }}>
            Upcoming
          </button>
          <button
            onClick={() => setTab('past')}
            style={{
              fontSize: 12, padding: '5px 14px', borderRadius: 17, border: 'none', cursor: 'pointer',
              background: tab === 'past' ? '#0d2b1a' : 'transparent',
              color:      tab === 'past' ? '#7ec99a' : '#5a5a4a',
              fontWeight: tab === 'past' ? 500 : 400,
            }}>
            Past
          </button>
        </div>
        {isPast && (
          <select
            style={{ fontSize: 12, padding: '5px 10px', borderRadius: 20, border: '1.5px solid #d8d4c8', background: '#fff', color: '#0d2b1a', cursor: 'pointer' }}
            value={rangeIdx}
            onChange={(e) => setRangeIdx(parseInt(e.target.value))}
          >
            {RANGES.map((r, i) => <option key={i} value={i}>{r.label}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading…</p></div>
      ) : teeTimes.length === 0 ? (
        <div className="empty-state">
          <p style={{ fontSize: 28 }}>{isPast ? '🕰️' : '🏌️'}</p>
          <p>{isPast ? 'No past tee times in this range.' : 'No tee times yet — browse or post one!'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {teeTimes.map(tt => {
            const d = fmtDate(tt.tee_date);
            const isHost = tt.host_id === user.id;
            const allGroupLabels = [
              ...(tt.is_everyone ? ['🌐 Everyone'] : []),
              ...(tt.groups || []).map(g => `${g.emoji} ${g.name}`),
            ];
            const grp = allGroupLabels.length > 0 ? allGroupLabels.join(', ') : null;
            const players = tt.players || [];
            return (
              <div key={tt.id} className="card"
                   style={{ display:'flex', alignItems:'flex-start', gap:'0.85rem', cursor:'pointer', opacity: isPast ? 0.85 : 1 }}
                   onClick={() => setDetailId(tt.id)}>
                <div style={{ textAlign:'center', minWidth:46, background: isPast ? '#5a5a4a' : '#0d2b1a', borderRadius:9, padding:'6px', color:'#fff', flexShrink:0 }}>
                  <div style={{ fontSize:8, textTransform:'uppercase', letterSpacing:'.08em', color: isPast ? '#c8c4b4' : '#7ec99a' }}>{d.m}</div>
                  <div style={{ fontSize:20, fontWeight:600, lineHeight:1, fontFamily:"'Playfair Display', serif" }}>{d.d}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight:500, fontSize:14, color:'#0d2b1a' }}>{tt.course_name}</div>
                  <div style={{ fontSize:12, color:'#7a7a6a', marginTop:1 }}>
                    {fmtTime(tt.tee_time)} · {tt.total_spots}-player · {tt.holes || 18} holes{grp ? ` · ${grp}` : ''}
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:5 }}>
                    {players.map(p => {
                      const isHostChip = p.id === tt.host_id;
                      const isSelf     = p.id === user.id;
                      const isGuest    = p.type === 'guest';
                      const gStatus    = isGuest ? (p.status || 'confirmed') : null;
                      const clickable  = !isSelf && !isGuest && !isHostChip;
                      return (
                        <span
                          key={p.id}
                          onClick={clickable ? (e) => { e.stopPropagation(); navigate(`/friends?user=${p.id}`); } : undefined}
                          className={`chip${isHostChip ? ' chip-host' : ''}${isSelf && !isHostChip ? ' chip-you' : ''}`}
                          style={{
                            cursor: clickable ? 'pointer' : 'default',
                            opacity: gStatus === 'declined' ? 0.55 : 1,
                            textDecoration: gStatus === 'declined' ? 'line-through' : 'none',
                          }}
                          title={clickable ? `View ${p.name}'s profile` : undefined}
                        >
                          {p.name}{isHostChip ? ' ★' : ''}{isSelf && !isHostChip ? ' (you)' : ''}
                          {gStatus === 'invited'  ? ' · invited'  : ''}
                          {gStatus === 'declined' ? ' · declined' : ''}
                        </span>
                      );
                    })}
                  </div>
                  <div style={{ marginTop:6, display:'flex', gap:5, flexWrap:'wrap' }}>
                    <button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); setDetailId(tt.id); }}>
                      Thread{tt.comment_count > 0 ? ` (${tt.comment_count})` : ''}
                    </button>
                    {!isPast && isHost && (
                      <button className="btn btn-danger btn-xs" onClick={e => { e.stopPropagation(); handleCancelTeeTime(tt); }}>Cancel tee time</button>
                    )}
                    {!isPast && !isHost && (
                      <button className="btn btn-danger btn-xs" onClick={e => { e.stopPropagation(); handleCancel(tt.id); }}>Cancel spot</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detailTT && (
        <TeeDetailModal
          tt={detailTT}
          currentUser={user}
          onClose={() => setDetailId(null)}
          onRefresh={load}
          readonly={isPast}
        />
      )}

      {cancelTT && (
        <CancelTeeTimeModal
          tt={cancelTT}
          currentUser={user}
          onClose={() => setCancelTT(null)}
          onConfirmed={() => { setCancelTT(null); load(); }}
        />
      )}
    </div>
  );
}
